// useReportPDFPrinter.js
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { formatDate } from '@/composables/useUtils';
import { useStatics } from '@/composables/useStatics';
import moment from 'moment';

export function useReportPDFPrinter(reportData, reportType, companyConfig, startDate, endDate) {
    const { brandName, brandWebSite } = useStatics();

    // Helper para formatear números con separadores
    const formatNum = (num, decimals = 0) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    };

    return new Promise((resolve) => {
        const doPrintAsync = async () => {
            const isDetailReport = (reportType === 'user_detail' || reportType === 'working_hours');
            const paperFormat = isDetailReport ? 'legal' : 'letter';

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: paperFormat
            });

            const margin = 15;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let currentY = 15;

            // --- 1. PROCESAMIENTO DE LOGO ---
            const logoUrl = companyConfig?.logo_url;
            if (logoUrl) {
                try {
                    const imgBase64 = await new Promise((res) => {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width; canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            res(canvas.toDataURL('image/png'));
                        };
                        img.onerror = () => res(null);
                        img.src = logoUrl;
                    });
                    if (imgBase64) {
                        doc.addImage(imgBase64, 'PNG', margin, currentY, 30, 15);
                    }
                } catch (e) { console.warn("Logo no disponible"); }
            }

            // --- 2. ENCABEZADO DETALLADO ---
            doc.setFontSize(14).setFont(undefined, 'bold').setTextColor(40);
            doc.text(companyConfig?.emitter_name || "SISTEMA DE GESTIÓN", margin + 35, currentY + 5);
            doc.setFontSize(8).setFont(undefined, 'normal').setTextColor(100);
            doc.text(`RUC: ${companyConfig?.emitter_ruc || '---'}`, margin + 35, currentY + 10);
            doc.text(`Dirección: ${companyConfig?.emitter_address || '---'}`, margin + 35, currentY + 14);

            let reportTitle = "REPORTE DE GESTIÓN";
            if (reportType === 'user_detail') reportTitle = "DETALLE DE TAREAS POR EMPLEADO";
            else if (reportType === 'summary') reportTitle = "RESUMEN DE RENDIMIENTO";
            else if (reportType === 'working_hours') reportTitle = "ASISTENCIA Y HORAS ACTIVAS";
            else if (reportType === 'task_type') reportTitle = "RESUMEN POR TIPO DE TAREA";
            else if (reportType === 'performance_by_branch.detail') reportTitle = "DETALLE DE RENDIMIENTO POR SUCURSAL";
            else if (reportType === 'performance_by_branch.summary') reportTitle = "RESUMEN CONSOLIDADO POR SUCURSAL";

            doc.setFontSize(12).setFont(undefined, 'bold').setTextColor(255, 128, 0);
            doc.text(reportTitle, pageWidth - margin, currentY + 5, { align: 'right' });
            doc.setFontSize(8).setTextColor(100);
            const dateText = (startDate === endDate) ? `Fecha: ${formatDate(startDate)}` : `Período: ${formatDate(startDate)} hasta ${formatDate(endDate)}`;
            doc.text(dateText, pageWidth - margin, currentY + 10, { align: 'right' });
            doc.text(`Impreso por: ${localStorage.getItem('userName') || 'Admin'}`, pageWidth - margin, currentY + 14, { align: 'right' });
            doc.setDrawColor(255, 128, 0).setLineWidth(0.5).line(margin, currentY + 18, pageWidth - margin, currentY + 18);
            currentY += 25;

            // --- 3. COLUMNAS ---
            let columns = [];
            if (reportType === 'user_detail') {
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'FECHA ASIG.', dataKey: 'assigned_at' },
                    { header: 'EMPLEADO', dataKey: 'employee_name' },
                    { header: 'TAREA', dataKey: 'task_description' },
                    { header: 'A COBRAR', dataKey: 'to_collect' },
                    { header: 'COBRADO', dataKey: 'collected' },
                    { header: 'ESTADO', dataKey: 'status' }
                ];
            } else if (reportType === 'summary') {
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'EMPLEADO', dataKey: 'employee_name' },
                    { header: 'TAREAS ASIGNADAS', dataKey: 'total_assigned' },
                    { header: 'TAREAS COMPLETADAS', dataKey: 'total_completed' }
                ];
            } else if (reportType === 'performance_by_branch.detail') {
                columns = [
                    { header: 'SUCURSAL', dataKey: 'branch_name' },
                    { header: 'ASESOR', dataKey: 'employee_name' },
                    { header: 'ASIGNADAS', dataKey: 'total_assigned' },
                    { header: 'COMPLETADAS', dataKey: 'total_completed' },
                    { header: '% TAREAS', dataKey: 'compliance_rate' },
                    { header: 'REC. PROYECTADO $', dataKey: 'assigned_amount' },
                    { header: 'REC. COBRADO $', dataKey: 'realized_amount' },
                    { header: '% COBRO', dataKey: 'collection_rate' }
                ];
            } else if (reportType === 'performance_by_branch.summary') {
                columns = [
                    { header: 'SUCURSAL', dataKey: 'branch_name' },
                    { header: 'TOTAL TAREAS ASIG.', dataKey: 'total_assigned' },
                    { header: 'TOTAL TAREAS REAL.', dataKey: 'total_completed' },
                    { header: 'TOTAL REC. PROYECTADO $', dataKey: 'assigned_amount' },
                    { header: 'TOTAL REC. COBRADO $', dataKey: 'realized_amount' }
                ];
            } else if (reportType === 'working_hours') {
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'EMPLEADO', dataKey: 'user_name' },
                    { header: 'FECHA', dataKey: 'work_date' },
                    { header: 'ENTRADA', dataKey: 'clock_in' },
                    { header: 'SALIDA', dataKey: 'clock_out' },
                    { header: 'TOTAL HORAS', dataKey: 'total_hours' },
                    { header: 'TAREAS OK', dataKey: 'tasks_completed' }
                ];
            } else if (reportType === 'task_type') {
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'TIPO DE TAREA', dataKey: 'type_name' },
                    { header: 'TOTAL ASIGNADAS', dataKey: 'total_tasks' },
                    { header: 'TOTAL COMPLETADAS', dataKey: 'completed' }
                ];
            }

            // --- 4. DATA Y CÁLCULO DE TOTALES ---
            let sum1 = 0, sum2 = 0, sum3 = 0, sum4 = 0;

            const finalBody = reportData.map((row, index) => {
                const newRow = { ...row, row_number: index + 1 };

                // Formateo de valores numéricos en las celdas
                if (newRow.assigned_amount) newRow.assigned_amount = formatNum(newRow.assigned_amount, 2);
                if (newRow.realized_amount) newRow.realized_amount = formatNum(newRow.realized_amount, 2);
                if (newRow.to_collect) newRow.to_collect = formatNum(newRow.to_collect, 2);
                if (newRow.collected) newRow.collected = formatNum(newRow.collected, 2);
                if (newRow.total_assigned) newRow.total_assigned = formatNum(newRow.total_assigned, 0);
                if (newRow.total_completed) newRow.total_completed = formatNum(newRow.total_completed, 0);

                Object.keys(newRow).forEach(k => { if(newRow[k] === null || newRow[k] === 'null') newRow[k] = '---'; });

                if (reportType === 'summary' || reportType === 'task_type') {
                    sum1 += parseInt(row.total_assigned || row.total_tasks || 0);
                    sum2 += parseInt(row.total_completed || row.completed || 0);
                } else if (reportType.includes('performance_by_branch')) {
                    sum1 += parseInt(row.total_assigned || 0);
                    sum2 += parseInt(row.total_completed || 0);
                    sum3 += parseFloat(row.assigned_amount || 0);
                    sum4 += parseFloat(row.realized_amount || 0);
                } else if (reportType === 'working_hours') {
                    sum1 += parseFloat(row.total_hours || 0);
                    sum2 += parseInt(row.tasks_completed || 0);
                } else if (reportType === 'user_detail') {
                    sum1 += parseFloat(row.to_collect || 0);
                    sum2 += parseFloat(row.collected || 0);
                }
                return newRow;
            });

            // --- 5. CONFIGURACIÓN DEL PIE DE TABLA (SOLO AL FINAL) ---
            let footData = new Array(columns.length).fill('');
            if (reportType === 'summary' || reportType === 'task_type') {
                footData[1] = 'TOTALES GENERALES:';
                footData[2] = formatNum(sum1, 0);
                footData[3] = formatNum(sum2, 0);
            } else if (reportType === 'performance_by_branch.detail') {
                footData[1] = 'GRAN TOTAL:';
                footData[2] = formatNum(sum1, 0);
                footData[3] = formatNum(sum2, 0);
                footData[4] = sum1 > 0 ? `${((sum2/sum1)*100).toFixed(2)}%` : '0%';
                footData[5] = `$ ${formatNum(sum3, 2)}`;
                footData[6] = `$ ${formatNum(sum4, 2)}`;
                footData[7] = sum3 > 0 ? `${((sum4/sum3)*100).toFixed(2)}%` : '0%';
            } else if (reportType === 'performance_by_branch.summary') {
                footData[0] = 'GRAN TOTAL:';
                footData[1] = formatNum(sum1, 0);
                footData[2] = formatNum(sum2, 0);
                footData[3] = `$ ${formatNum(sum3, 2)}`;
                footData[4] = `$ ${formatNum(sum4, 2)}`;
            } else if (reportType === 'working_hours') {
                footData[4] = 'TOTALES:';
                footData[5] = `${formatNum(sum1, 2)} h`;
                footData[6] = formatNum(sum2, 0);
            } else if (reportType === 'user_detail') {
                footData[3] = 'TOTALES:';
                footData[4] = `$ ${formatNum(sum1, 2)}`;
                footData[5] = `$ ${formatNum(sum2, 2)}`;
            }

            // --- 6. DIBUJAR TABLA ---
            doc.autoTable({
                columns: columns,
                body: [
                    ...finalBody,
                    footData.map(cell => ({
                        content: cell,
                        styles: { fillColor: [230, 230, 230], textColor: 0, fontStyle: 'bold', fontSize: 8 }
                    }))
                ],
                startY: currentY,
                margin: { left: margin, right: margin },
                theme: 'striped',
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [255, 128, 0], textColor: 255, fontStyle: 'bold' },
            });

            // --- 7. FOOTER ---
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(6).setTextColor(150);
                const footerText = `Servicio provisto por: ${brandName}  |  `;
                const textWidth = doc.getTextWidth(footerText);
                doc.text(footerText, margin, pageHeight - 10);
                doc.setTextColor(0, 114, 198);
                doc.text(brandWebSite, margin + textWidth, pageHeight - 10);
                doc.link(margin + textWidth, pageHeight - 13, doc.getTextWidth(brandWebSite), 5, { url: `https://${brandWebSite}` });
                doc.setTextColor(0);
                doc.setFontSize(8);
                doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
            }

            doc.save(`Reporte_${reportTitle.replace(/ /g, "_")}_${moment().format('YYYYMMDD')}.pdf`);
            resolve();
        };
        doPrintAsync();
    });
}